<script>

import isEmpty from "is-empty";
import Layout from "../../layouts/auth";
import {
  authMethods,
  notificationMethods,
} from "@/state/helpers";

import appConfig from "@/app.config";
import { required, email } from "vuelidate/lib/validators";
import AdminUtils from "@/helpers/admin";
import AuthService from "@/services/AuthService";
import * as qrcode from 'qrcode';
import * as crypto from "crypto";

/**
 * Login component
 */
export default {
  page: {
    localePicker: () => import(`vue2-datepicker/locale/${this.$i18n.locale}`),
    title: "Login",
    meta: [
      {
        name: "description",
        content: appConfig.description
      },
    ],
  },
  components: {
    Layout,
  },
  data() {
    return {
      isLoading: false,
      email: "",
      password: "",
      submitted: false,
      authError: null,
      tryingToLogIn: false,
      isAuthError: false,
      adminId: null,
      otpToken: null,
      otpSecret: null,
      otpUri: null,
      qrcodeUri: '',
      otpProcessing: false,
    };
  },
  validations: {
    email: {
      required,
      email,
    },
    password: {
      required,
    },
  },
  computed: {
    notification() {
      return this.$store ? this.$store.state.notification : null;
    },
  },
  methods: {
    ...authMethods,
    ...notificationMethods,
    // Try to log the user in with the username
    // and password they provided.
    async tryToLogIn() {
      this.submitted = true;
      // stop here if form is invalid
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      } else {
        this.isLoading = true;
        this.tryingToLogIn = true;
        // Reset the authError if it existed.
        this.authError = null;
        await this.checkUser();
      }
    },

    async checkUser() {
      const passwd = crypto.createHash("sha256").update(this.password).digest("hex");
      AuthService.checkUser({
        email: this.email,
        passwd: passwd,
      }).then(async (response) => {
        if (isEmpty(response.data.adminId)) {
          this.$toast.error("Invalid credentials");
          this.tryingToLogIn = false;
          this.isLoading = false;
          return;
        }

        this.adminId = response.data.adminId;

        if (response.data.otp === false) {
          await this.finishLogin();
        }
        else if (!isEmpty(response.data.otpUri) && !isEmpty(response.data.secret)) {
          this.otpUri = response.data.otpUri;
          this.otpSecret = response.data.secret;
          this.showOTPVerification(response.data);
        }
        else {
          this.showOTPValidation();
        }
      })
      .catch(error => {
        this.$toast.error(error.message);
      });
    },

    async verifyOTP() {
      this.otpProcessing = true;
      AuthService.verifyOTP({
        adminId: this.adminId,
        token: this.otpToken
      }).then(async (response) => {
        if (response.data.otp_verified === true) {
          this.otpSecret = null;
          this.closeModal();
          await this.finishLogin();
        }
        else {
          this.otpProcessing = false;
          this.$toast.error("ERROR: invalid token");
        }
      })
      .catch(error => {
        this.otpProcessing = false;
        this.$toast.error(error.message);
      });
    },

    async validateOTP() {
      if (this.otpSecret !== null) {
        await this.verifyOTP();
        return;
      }

      this.otpProcessing = true;
      AuthService.validateOTP({
        adminId: this.adminId,
        token: this.otpToken
      }).then(async (response) => {
        if (response.data.otp_valid === true) {
          this.closeModal();
          await this.finishLogin();
        }
        else {
          this.otpProcessing = false;
          this.$toast.error("ERROR: invalid token");
        }
      })
      .catch(error => {
        this.otpProcessing = false;
        this.$toast.error(error.message);
      });
    },

    async finishLogin() {
      const result = await this.logIn({
        email: this.email,
        passwd: this.password
      });

      this.isLoading = false;
      if (result.error || !result.user) {
        this.tryingToLogIn = false;
        this.authError = result.error ? result.error : "";
        this.isAuthError = true;
        return;
      }

      this.tryingToLogIn = false;
      this.isAuthError = false;

      await AdminUtils.addAction({
        adminId: this.adminId,
        category: 'session',
        type: 'login'
      });
      await this.$router.push(this.$route.query.redirectedFrom || {
        path: "/",
      });
    },

    showOTPVerification() {
      this.showModal();
      qrcode.toDataURL(this.otpUri, (err, url) => {
        this.qrcodeUri = url;
      });
    },

    showOTPValidation() {
      this.showModal();
      setTimeout(() => {
        document.getElementById("loginOtp").focus();
      }, 500);
    },

    showModal() {
      this.$bvModal.show("otp-modal");
    },

    closeModal() {
      this.$bvModal.hide("otp-modal");
    }
  },
  mounted() {},
  watch: {
    otpToken: {
      handler: function (newVal) {
        if (newVal.length === 6)
          this.validateOTP();
      }
    }
  },
};
</script>

<template>
  <Layout>
    <div class="row justify-content-center">
      <div class="col-md-8 col-lg-6 col-xl-5">
        <img src="@/assets/images/logo-dark.png" alt height="50" class="d-block mx-auto mb-4" />
        <div class="card overflow-hidden">
          <div class="bg-primary bg-soft">
            <div class="row">
              <div class="col-7">
                <div class="text-primary p-4">
                  <h5 class="text-primary">Welcome Back !</h5>
                  <p>Sign in to continue to Spreedl BackOffice.</p>
                </div>
              </div>
              <div class="col-5 align-self-end">
                <img
                  src="@/assets/images/profile-img.png"
                  alt
                  class="img-fluid"
                />
              </div>
            </div>
          </div>
          <div class="card-body pt-0">
            <b-alert
              v-model="isAuthError"
              variant="danger"
              class="mt-3"
              dismissible
              >{{ authError }}</b-alert
            >

            <div
              v-if="notification.message"
              :class="'alert ' + notification.type"
            >
              {{ notification.message }}
            </div>

            <b-form class="p-2" @submit.prevent="tryToLogIn">
              <b-form-group
                class="mb-3"
                id="input-group-1"
                label="Email"
                label-for="input-1"
              >
                <b-form-input
                  id="input-1"
                  v-model="email"
                  type="text"
                  placeholder="Enter email"
                  :class="{ 'is-invalid': submitted && $v.email.$error }"
                ></b-form-input>
                <div
                  v-if="submitted && $v.email.$error"
                  class="invalid-feedback"
                >
                  <span v-if="!$v.email.required">Email is required.</span>
                  <span v-if="!$v.email.email">Please enter valid email.</span>
                </div>
              </b-form-group>

              <b-form-group
                class="mb-3"
                id="input-group-2"
                label="Password"
                label-for="input-2"
              >
                <b-form-input
                  id="input-2"
                  v-model="password"
                  type="password"
                  placeholder="Enter password"
                  :class="{ 'is-invalid': submitted && $v.password.$error }"
                ></b-form-input>
                <div
                  v-if="submitted && !$v.password.required"
                  class="invalid-feedback"
                >
                  Password is required.
                </div>
              </b-form-group>
              <b-form-checkbox
                  class="form-check"
                  id="customControlInline"
                  name="checkbox-1"
                  value="accepted"
                  unchecked-value="not_accepted"
                >
                  Remember me
                </b-form-checkbox>
              <div class="mt-3 d-grid">
                <b-button type="submit" variant="primary" class="btn-block" v-id="!isLoading"
                  >Log In</b-button
                >
                <div class="row d-flex justify-content-center">
                  <vue-ellipse-progress
                      v-show="isLoading"
                      :loading="isLoading"
                      :angle="-90"
                      color="#50a5f1"
                      emptyColor="#999"
                      emptyColorFill="#f8f8fb"
                      :size="50"
                      :thickness="3"
                      :emptyThickness="2"
                      lineMode="out 2"
                      animation="bounce 700 400"
                      fontColor="#666"
                      :half="false"
                      class="mt-5 mb-5"
                      fontSize="1rem">
                  </vue-ellipse-progress>
                </div>
              </div>
              <div class="mt-4 text-center">
                <router-link tag="a" to="/forgot-password" class="text-muted">
                  <i class="mdi mdi-lock me-1"></i> Forgot your password?
                </router-link>
              </div>
            </b-form>
          </div>
          <!-- end card-body -->
        </div>
        <!-- end card -->

        <div class="mt-5 text-center">
          <p>
            © {{ new Date().getFullYear() }} Pickup Agency. Crafted with
            <i class="mdi mdi-heart text-danger"></i>
          </p>
        </div>
        <!-- end row -->
      </div>
      <!-- end col -->
    </div>
    <!-- end row -->

    <b-modal id="otp-modal" hide-footer>
      <template #modal-title>
        2FA Verification
      </template>
      <div>
        <div v-show="otpSecret !== null">
          <h5 class="text-primary">
            Configuring Google Authenticator or Authy
          </h5>
          <div class="text-muted">
            <li>
              Install Google Authenticator (IOS - Android) or Authy (IOS -
              Android).
            </li>
            <li>In the authenticator app, select "+" icon.</li>
            <li>
              Select "Scan a barcode (or QR code)" and use the phone's camera
              to scan this barcode.
            </li>
          </div>
          <h5 class="text-primary mt-3">Scan QR Code</h5>
          <div class="row d-flex justify-content-center">
            <img
                class="object-fit-contain"
                :src="qrcodeUri"
                style="width:250px;"
                alt="qrcode url"
            />
          </div>
          <div>
            <h5 class="text-primary mt-2">Or Enter Code Into Your App</h5>
            <p class="text-muted">SecretKey: <b>{{otpSecret}}</b><small><br>(Base32 encoded)</small></p>
          </div>
        </div>
        <div>
          <h5 class="text-primary">Verify Code</h5>
          <p class="text-muted">
            For changing the setting, please verify the authentication code:
          </p>
        </div>
        <!-- Loader -->
        <div class="row d-flex justify-content-center">
          <vue-ellipse-progress
              v-show="otpProcessing"
              :loading="otpProcessing"
              :angle="-90"
              color="#50a5f1"
              emptyColor="#999"
              emptyColorFill="#f8f8fb"
              :size="100"
              :thickness="3"
              :emptyThickness="2"
              lineMode="out 2"
              animation="bounce 700 400"
              fontColor="#666"
              :half="false"
              class="mt-5 mb-5"
              fontSize="1rem">
          </vue-ellipse-progress>
        </div>
        <form v-show="!otpProcessing">
          <b-form-input
              id="loginOtp"
              class=""
              v-model="otpToken"
              placeholder="Enter code…">
          </b-form-input>

          <div class="mt-2">
            <b-button
                variant="outline-danger"
                class="me-2"
                @click="closeModal"
            >
              Close
            </b-button>
            <b-button variant="primary" @click="validateOTP()">
              Verify & Activate
            </b-button>
          </div>
        </form>
      </div>
    </b-modal>

  </Layout>
</template>

<style lang="scss" module></style>
